/**
 * Supplier Profile Layout Tab — toggles which stat cards, top-level tabs and
 * Overview widgets appear on /suppliers/:id. Platform-wide setting so each
 * tenant instance gets a profile view that matches the data they actually
 * collect (e.g. MAB hides Performance/Orders because they don't track POs).
 */
import { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../ui/card';
import { Button } from '../../ui/button';
import { Switch } from '../../ui/switch';
import { Label } from '../../ui/label';
import { Building2, Layout } from 'lucide-react';
import { platformSettingsAPI, type SupplierProfileLayout } from '../../../lib/api';
import { toast } from 'sonner';
import { useMenuSettings } from '../../../contexts/MenuSettingsContext';

const STAT_FIELDS: Array<{ id: keyof SupplierProfileLayout['stats']; label: string; description: string }> = [
  { id: 'otd', label: 'On-Time Delivery card', description: 'Top-of-profile OTD % stat card.' },
  { id: 'total_orders', label: 'Total Orders card', description: 'Top-of-profile PO count stat card.' },
];

const TAB_FIELDS: Array<{ id: keyof SupplierProfileLayout['tabs']; label: string; description: string }> = [
  { id: 'performance', label: 'Performance', description: 'OTIF/OTD/quality dashboards — hide if no GRN data.' },
  { id: 'orders', label: 'Orders', description: 'Purchase orders list — hide if no PO data.' },
  { id: 'contracts', label: 'Contracts', description: 'Contract register.' },
  { id: 'quality', label: 'Quality', description: 'NCR + concession register for this supplier.' },
  { id: 'questionnaires', label: 'Questionnaires', description: 'Compliance questionnaires assigned to this supplier.' },
  { id: 'credit_scores', label: 'Credit Scores', description: 'Recorded credit scores over time.' },
  { id: 'users', label: 'Users', description: 'Supplier-side user accounts.' },
  { id: 'activity_log', label: 'Activity Log', description: 'Auditable timeline of profile changes.' },
  { id: 'group_structure', label: 'Group Structure', description: 'Supplier group hierarchy tab.' },
];

// CR-4821: Performance Summary card was removed from Company Overview tab —
// toggle is no longer exposed to admins. Capabilities remains configurable.
const OVERVIEW_FIELDS: Array<{ id: keyof SupplierProfileLayout['overview']; label: string; description: string }> = [
  { id: 'capabilities', label: 'Capabilities block', description: 'Capability chips on the Company Overview tab.' },
];

export function SupplierProfileLayoutTab() {
  const { supplierProfileLayout, updateSupplierProfileLayout } = useMenuSettings();
  const [layout, setLayout] = useState<SupplierProfileLayout>(supplierProfileLayout);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setLayout(supplierProfileLayout);
  }, [supplierProfileLayout]);

  const setStat = (id: keyof SupplierProfileLayout['stats'], val: boolean) =>
    setLayout(prev => ({ ...prev, stats: { ...prev.stats, [id]: val } }));
  const setTab = (id: keyof SupplierProfileLayout['tabs'], val: boolean) =>
    setLayout(prev => ({ ...prev, tabs: { ...prev.tabs, [id]: val } }));
  const setOverview = (id: keyof SupplierProfileLayout['overview'], val: boolean) =>
    setLayout(prev => ({ ...prev, overview: { ...prev.overview, [id]: val } }));

  const handleSave = async () => {
    setSaving(true);
    try {
      await platformSettingsAPI.patchSettings({ supplier_profile_layout: layout } as unknown as Record<string, unknown>);
      updateSupplierProfileLayout(layout);
      toast.success('Supplier Profile layout saved');
    } catch {
      toast.error('Failed to save Supplier Profile layout');
    } finally {
      setSaving(false);
    }
  };

  const renderRow = (def: { id: string; label: string; description: string }, checked: boolean, onChange: (v: boolean) => void) => (
    <div key={def.id} className="flex items-center justify-between gap-3 p-3 border rounded-lg">
      <div className="flex-1">
        <Label className="text-sm font-medium">{def.label}</Label>
        <p className="text-xs text-muted-foreground mt-0.5">{def.description}</p>
      </div>
      <Switch checked={checked} onCheckedChange={onChange} />
    </div>
  );

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Building2 className="h-5 w-5" /> Supplier Profile Layout
          </CardTitle>
          <CardDescription>
            Control which parts of the Supplier Profile page ({"/suppliers/:id"}) are visible
            for this tenant. Overview, Company Information, and Settings are always shown.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          <section className="space-y-3">
            <h3 className="text-sm font-semibold flex items-center gap-2 text-muted-foreground uppercase">
              <Layout className="h-4 w-4" /> Header Stat Cards
            </h3>
            {STAT_FIELDS.map(f => renderRow({ id: String(f.id), label: f.label, description: f.description }, layout.stats[f.id] ?? true, v => setStat(f.id, v)))}
          </section>

          <section className="space-y-3">
            <h3 className="text-sm font-semibold text-muted-foreground uppercase">Profile Tabs</h3>
            {TAB_FIELDS.map(f => renderRow({ id: String(f.id), label: f.label, description: f.description }, layout.tabs[f.id] ?? true, v => setTab(f.id, v)))}
          </section>

          <section className="space-y-3">
            <h3 className="text-sm font-semibold text-muted-foreground uppercase">Overview Widgets</h3>
            {OVERVIEW_FIELDS.map(f => renderRow({ id: String(f.id), label: f.label, description: f.description }, layout.overview[f.id] ?? true, v => setOverview(f.id, v)))}
          </section>

          <Button className="w-full" disabled={saving} onClick={handleSave}>
            {saving ? 'Saving...' : 'Save Layout'}
          </Button>
        </CardContent>
      </Card>
    </div>
  );
}
